// src/pages/container_rental/components/timeline/RentalBar.tsx
//
// 행 하나의 트랙. 행당 <svg> 한 개만 쓴다(요소 수를 아끼려고 세그먼트를 rect 로 그린다).
//
// 그리는 것들
//  - 바 프레임 : 상태별 채움/테두리(draft 점선, closed 절단, cancelled 는 행 opacity)
//  - 회차 세그먼트 : 상태별 채움. 빗금은 TimelineDefs 의 공유 <pattern> 참조.
//  - 1회차 INIT 표식 : 앰버 언더라인 + 라벨
//  - issueDate 삼각 틱
//  - 연체 세그먼트 : stop 2px 테두리 + 틴트 (서버 overdue 플래그만 신뢰)
//  - D-90 만료 예고 앰버 캡 (active 계약만)
//  - paused : 바 전체 앰버 빗금 오버레이 + PAUSED 라벨
//  - 도메인(보이는 기간) 밖으로 이어지는 바 : 잘림 표식(◀ / ▶)
//
// 성능: 도메인 밖 세그먼트는 아예 그리지 않고, [viewFrom, viewTo] 밖(가로
// 스크롤로 화면에 없는 구간)도 건너뛴다. 회차 수백 개 × 행 수십 개가
// 한꺼번에 rect 로 쌓이는 것을 막는다.
//
// Phase 1 은 조회 전용 — 발행/건너뜀 같은 액션 버튼은 없다.

import { useMemo, useState } from "react";
import { Box, Button, Popover, Tooltip } from "@mui/material";
import type { Dayjs } from "dayjs";

import { designTokens } from "../../../../theme.ts";
import { fmtDate } from "../../../../utils/date.ts";
import type { TimelineRowDto } from "../../../../contracts/container_rental/timeline";
import {
  MARK,
  PATTERN_ID,
  rentalVisual,
  roundVisual,
} from "../statusVisuals.ts";
import { clampDate, dateToX, dayDiff } from "./TimelineScale.ts";
import {
  barRange,
  fmtKrw,
  isExpiringSoon,
  rowSegments,
} from "./timelineModel.ts";
import type { SegmentModel } from "./timelineModel.ts";

const T = designTokens;

/** 확정 디자인 시스템의 중립 토큰(designTokens 에 없는 보더/서브/필드 배경). */
const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  neutral: "#F2F4F5",
};

export const BAR_H = 24;

interface RentalBarProps {
  row: TimelineRowDto;
  /** 도메인 시작(= x 0) */
  origin: Dayjs;
  /** 도메인 끝(포함). 이 밖으로 나간 바·세그먼트는 잘라 그린다. */
  domainEnd: Dayjs;
  ppd: number;
  /** 서버 range.today (KST) */
  today: Dayjs;
  /** 트랙 전체 폭 */
  width: number;
  /** 행 높이 */
  height: number;
  /** 가로 컬링 창(px, 트랙 좌표). null 이면 컬링하지 않는다. */
  viewFrom?: number | null;
  viewTo?: number | null;
  onOpenDetail: (id: string) => void;
}

export default function RentalBar({
  row,
  origin,
  domainEnd,
  ppd,
  today,
  width,
  height,
  viewFrom = null,
  viewTo = null,
  onOpenDetail,
}: RentalBarProps) {
  const [hover, setHover] = useState<SegmentModel | null>(null);
  const [popover, setPopover] = useState<{
    anchor: Element;
    seg: SegmentModel;
  } | null>(null);

  const visual = rentalVisual(row.status);
  const segs = useMemo(() => rowSegments(row), [row]);

  const bar = useMemo(() => barRange(row, today), [row, today]);
  const barTop = Math.round((height - BAR_H) / 2);

  // 도메인 밖으로 나간 바는 잘라서 그린다(요청 기간을 좁게 잡으면 도메인도
  // 그 창으로 제한되므로, 창 밖으로 뻗은 계약이 있어도 차트 폭이 늘지 않는다).
  const clippedStart = dayDiff(origin, bar.start) < 0;
  const clippedEnd = dayDiff(bar.end, domainEnd) < 0;
  const drawStart = clampDate(bar.start, origin, domainEnd);
  const drawEnd = clampDate(bar.end, origin, domainEnd);
  const x0 = dateToX(origin, drawStart, ppd);
  const x1 = dateToX(origin, drawEnd, ppd) + ppd;
  const barW = Math.max(x1 - x0, 3);

  /** [a, b] 가 가로 컬링 창과 겹치는가. 창이 없으면 항상 그린다. */
  const inView = (a: number, b: number) =>
    viewFrom == null || viewTo == null || (b >= viewFrom && a <= viewTo);

  // 잘린 바 끝에는 "여기서 끝난 게 아니다"라는 표식을 붙여야 하므로
  // closed 절단 표식 / D-90 캡은 끝이 잘리지 않았을 때만 그린다.
  const expiring = isExpiringSoon(row, today) && !clippedEnd;
  const daysLeft = expiring ? dayDiff(today, bar.end) : 0;

  const tooltipTitle = hover
    ? segmentTooltip(hover)
    : [
        `${row.clientName || "—"} · ${fmtDate(bar.start)} → ${fmtDate(bar.end)}${
          bar.estimated ? " (추정)" : ""
        }`,
        clippedStart || clippedEnd ? "· 선택한 기간 밖으로 이어짐" : "",
      ]
        .filter(Boolean)
        .join("\n");

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        enterDelay={300}
        followCursor
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              borderRadius: "8px",
              bgcolor: T.ink,
              fontFamily: T.fonts.sans,
              fontSize: 11.5,
              whiteSpace: "pre-line",
              px: 1.1,
              py: 0.7,
            },
          },
        }}
      >
        <svg
          width={width}
          height={height}
          style={{ display: "block" }}
          onMouseLeave={() => setHover(null)}
        >
          {/* 바 프레임 */}
          <rect
            x={x0}
            y={barTop}
            width={barW}
            height={BAR_H}
            fill={visual.trackFill}
            stroke={visual.frameStroke}
            strokeWidth={1}
            strokeDasharray={visual.frameDash}
            shapeRendering="crispEdges"
          />

          {/* 회차 정보가 없어 추정한 구간이면 옅은 빗금으로 표시 */}
          {bar.estimated && (
            <rect
              x={x0 + 1}
              y={barTop + 1}
              width={Math.max(barW - 2, 1)}
              height={BAR_H - 2}
              fill={`url(#${PATTERN_ID.ghostHatch})`}
              pointerEvents="none"
            />
          )}

          {/* 회차 세그먼트 */}
          {segs.map((s) => {
            // 도메인 밖 세그먼트는 그리지 않는다.
            if (dayDiff(origin, s.end) < 0 || dayDiff(s.start, domainEnd) < 0) {
              return null;
            }
            const v = roundVisual(s.schedule.status, s.schedule.overdue);
            const segStart = clampDate(s.start, origin, domainEnd);
            const segEnd = clampDate(s.end, origin, domainEnd);
            const sx = dateToX(origin, segStart, ppd);
            const sw = Math.max((dayDiff(segStart, segEnd) + 1) * ppd, 2);
            // 화면 밖(가로 스크롤 기준)도 건너뛴다.
            if (!inView(sx, sx + sw)) return null;
            const issueX = s.issue ? dateToX(origin, s.issue, ppd) : 0;
            const issueVisible =
              Boolean(s.issue) &&
              dayDiff(origin, s.issue as Dayjs) >= 0 &&
              dayDiff(s.issue as Dayjs, domainEnd) >= 0;
            return (
              <g key={s.schedule.round}>
                <rect
                  x={sx + 0.5}
                  y={barTop + 1}
                  width={Math.max(sw - 1, 1.5)}
                  height={BAR_H - 2}
                  fill={v.fill}
                  stroke={v.stroke}
                  strokeWidth={v.strokeWidth}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover(s)}
                  // 세그먼트 → 같은 행의 빈 트랙으로 나가도 hover 가 남지 않게.
                  // 이웃 세그먼트로 넘어갈 때는 그쪽 onMouseEnter 가 곧바로
                  // 덮으므로 툴팁이 깜빡이지 않는다.
                  onMouseLeave={() => setHover((cur) => (cur === s ? null : cur))}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPopover({ anchor: e.currentTarget, seg: s });
                  }}
                />
                {sw >= 22 && (
                  <text
                    x={sx + sw / 2}
                    y={barTop + BAR_H / 2 + 3}
                    textAnchor="middle"
                    fontFamily={T.fonts.mono}
                    fontSize={9}
                    fontWeight={700}
                    fill={v.labelColor}
                    pointerEvents="none"
                  >
                    {String(s.schedule.round).padStart(2, "0")}
                  </text>
                )}
                {/* 1회차 INIT 표식 — 앰버 언더라인 */}
                {s.schedule.round === 1 && (
                  <>
                    <rect
                      x={sx + 0.5}
                      y={barTop + BAR_H - 3}
                      width={Math.max(sw - 1, 1.5)}
                      height={3}
                      fill={MARK.init}
                      pointerEvents="none"
                    />
                    {sw >= 34 && (
                      <text
                        x={sx + 2}
                        y={barTop - 4}
                        fontFamily={T.fonts.sans}
                        fontSize={9}
                        fontWeight={700}
                        fill="#8A5C12"
                        pointerEvents="none"
                      >
                        1회차
                      </text>
                    )}
                  </>
                )}
                {/* 발행 예정일 삼각 틱 — 도메인 밖이면 그리지 않는다 */}
                {issueVisible && (
                  <polygon
                    points={trianglePoints(issueX + ppd / 2, barTop)}
                    fill={s.schedule.overdue ? MARK.overdue : MARK.issueTick}
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}

          {/* paused — 바 전체 앰버 빗금 오버레이 */}
          {visual.overlayPatternId && (
            <>
              <rect
                x={x0 + 1}
                y={barTop + 1}
                width={Math.max(barW - 2, 1)}
                height={BAR_H - 2}
                fill={`url(#${visual.overlayPatternId})`}
                opacity={0.75}
                pointerEvents="none"
              />
              {barW >= 60 && (
                <text
                  x={x0 + 6}
                  y={barTop + BAR_H / 2 + 3.5}
                  fontFamily={T.fonts.sans}
                  fontSize={9.5}
                  fontWeight={700}
                  fill={T.ink}
                  pointerEvents="none"
                >
                  {visual.ko}
                </text>
              )}
            </>
          )}

          {/* 도메인 밖으로 이어짐 — 잘림 표식 */}
          {clippedStart && (
            <polygon
              points={`${x0 + 6},${barTop + 1} ${x0 + 6},${barTop + BAR_H - 1} ${x0},${
                barTop + BAR_H / 2
              }`}
              fill={T.ink}
              pointerEvents="none"
            />
          )}
          {clippedEnd && (
            <polygon
              points={`${x1 - 6},${barTop + 1} ${x1 - 6},${barTop + BAR_H - 1} ${x1},${
                barTop + BAR_H / 2
              }`}
              fill={T.ink}
              pointerEvents="none"
            />
          )}

          {/* closed — 바 끝 절단 표식 */}
          {visual.truncatedEnd && !clippedEnd && (
            <g pointerEvents="none">
              <rect x={x1 - 5} y={barTop} width={2} height={BAR_H} fill={T.ink} />
              <rect x={x1 - 1} y={barTop} width={2} height={BAR_H} fill={T.ink} />
            </g>
          )}

          {/* D-90 만료 예고 앰버 캡 (active 만) */}
          {expiring && (
            <g pointerEvents="none">
              <rect
                x={x1 - 6}
                y={barTop - 2}
                width={6}
                height={BAR_H + 4}
                fill={MARK.expiring}
                stroke={T.ink}
                strokeWidth={1}
              />
              <text
                x={x1 + 5}
                y={barTop + BAR_H / 2 + 3.5}
                fontFamily={T.fonts.mono}
                fontSize={9}
                fontWeight={700}
                fill="#8A5C12"
              >
                {`D-${Math.max(daysLeft, 0)}`}
              </text>
            </g>
          )}
        </svg>
      </Tooltip>

      <Popover
        open={Boolean(popover)}
        anchorEl={popover?.anchor ?? null}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              border: `1px solid ${C.border}`,
              boxShadow: "0 8px 24px rgba(16,24,32,0.08)",
              bgcolor: T.paper,
              minWidth: 268,
              overflow: "hidden",
            },
          },
        }}
      >
        {popover && (
          <SegmentDetail
            row={row}
            seg={popover.seg}
            onOpenDetail={() => {
              setPopover(null);
              onOpenDetail(row.id);
            }}
          />
        )}
      </Popover>
    </>
  );
}

/* ------------------------------------------------------------------ */

function trianglePoints(cx: number, barTop: number): string {
  const half = 4;
  const top = barTop - 7;
  const bottom = barTop - 1;
  return `${cx - half},${top} ${cx + half},${top} ${cx},${bottom}`;
}

function segmentTooltip(s: SegmentModel): string {
  const v = roundVisual(s.schedule.status, s.schedule.overdue);
  return [
    `R.${String(s.schedule.round).padStart(2, "0")} · ${v.ko}`,
    `${fmtDate(s.start)} → ${fmtDate(s.end)}`,
    `발행 예정 ${fmtDate(s.issue)} · 합계 ${fmtKrw(s.schedule.total)}원`,
  ].join("\n");
}

function SegmentDetail({
  row,
  seg,
  onOpenDetail,
}: {
  row: TimelineRowDto;
  seg: SegmentModel;
  onOpenDetail: () => void;
}) {
  const v = roundVisual(seg.schedule.status, seg.schedule.overdue);
  return (
    <Box sx={{ fontFamily: T.fonts.sans }}>
      <Box
        sx={{
          bgcolor: C.field,
          borderBottom: `1px solid ${C.border}`,
          color: C.ink,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 13,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {seg.schedule.round}회차
        </Box>
        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12,
            fontWeight: 600,
            color: seg.schedule.overdue ? T.stop : v.accent,
          }}
        >
          {v.ko}
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Box
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: T.ink,
            mb: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.clientName || "—"}
          <Box component="span" sx={{ color: C.sub, fontWeight: 500 }}>
            {row.siteName ? ` · ${row.siteName}` : ""}
          </Box>
        </Box>

        <DetailRow label="사용기간" value={`${fmtDate(seg.start)} → ${fmtDate(seg.end)}`} />
        <DetailRow label="발행 예정" value={fmtDate(seg.issue)} />
        <DetailRow label="공급가" value={`${fmtKrw(seg.schedule.supply)} 원`} />
        <DetailRow label="합계" value={`${fmtKrw(seg.schedule.total)} 원`} strong />
        <DetailRow label="승인번호" value={seg.schedule.invoiceNumber || "—"} />
        <DetailRow label="사유" value={seg.schedule.issuedMemo || "—"} />

        <Button
          fullWidth
          variant="outlined"
          onClick={onOpenDetail}
          sx={{ mt: 1.5 }}
        >
          상세 보기
        </Button>
      </Box>
    </Box>
  );
}

function DetailRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1.5,
        py: 0.5,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 12,
          fontWeight: 500,
          color: C.sub,
          flexShrink: 0,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: T.fonts.mono,
          fontSize: strong ? 13 : 12,
          fontWeight: strong ? 700 : 600,
          color: C.ink,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
          wordBreak: "break-all",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}
